import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import padelService, { PadelGameDetail } from '@/services/padelService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { Card, CardContent, CardHeader } from '@/components/shadcn/card';
import Notification from '@/components/ui/Notification';
import PadelMatchScorePanel from '@/components/community/padel/PadelMatchScorePanel';
import PadelSetScorePanel from '@/components/community/padel/PadelSetScorePanel';
import { formatPadelGameStatus, playersLabel } from '@/components/community/padel/padelUiUtils';
import {
  buildSetsFromGame,
  isSet3Allowed,
  PADEL_MAX_SCORE,
  validateAmericanoMatchScore,
  type SetScoreInput,
} from '@/components/community/padel/padelScoringUtils';
import { isCustomerAppPath } from '@/utils/clientPaths';

export default function PadelRekapPage() {
  const { gameSlug, slug: eventSlugParam } = useParams<{ gameSlug: string; slug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientView = isCustomerAppPath(location.pathname);
  const [game, setGame] = useState<PadelGameDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [sets, setSets] = useState<SetScoreInput[]>([]);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({ isOpen: false, message: '', variant: 'success' });

  const loadGame = () => {
    if (!gameSlug) return;
    setLoading(true);
    padelService
      .getGame(gameSlug)
      .then((g) => {
        setGame(g);
        setScoreA(g.scoreSideA ?? 0);
        setScoreB(g.scoreSideB ?? 0);
        setSets(buildSetsFromGame(g.setScores ?? []));
      })
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadGame();
  }, [gameSlug]);

  const evSlug = game?.eventSlug ?? eventSlugParam;
  const backUrl = evSlug
    ? isClientView
      ? `/event/${evSlug}/padel`
      : `/host/mabar/${evSlug}/padel`
    : isClientView
      ? '/event'
      : '/host/mabar';

  const handleSave = async () => {
    if (!gameSlug || !game?.canEdit) return;

    if (game.scoringType === 'sets') {
      if (!isSet3Allowed(sets)) {
        const set3 = sets.find((s) => s.setNumber === 3);
        if (set3 && (set3.scoreSideA > 0 || set3.scoreSideB > 0)) {
          setNotification({
            isOpen: true,
            message: 'Set 3 belum dapat diisi sebelum masing-masing tim menang 1 set',
            variant: 'error',
          });
          return;
        }
      }
      for (const s of sets) {
        if (s.scoreSideA > PADEL_MAX_SCORE || s.scoreSideB > PADEL_MAX_SCORE) {
          setNotification({
            isOpen: true,
            message: `Skor maksimal ${PADEL_MAX_SCORE} per set`,
            variant: 'error',
          });
          return;
        }
      }
    } else if (scoreA > PADEL_MAX_SCORE || scoreB > PADEL_MAX_SCORE) {
      setNotification({
        isOpen: true,
        message: `Skor maksimal ${PADEL_MAX_SCORE}`,
        variant: 'error',
      });
      return;
    }

    if (game.scoringType === 'points' || game.scoringType === 'time') {
      const americanoErr = validateAmericanoMatchScore(scoreA, scoreB, game.targetScore);
      if (americanoErr) {
        setNotification({ isOpen: true, message: americanoErr, variant: 'error' });
        return;
      }
    }

    setSaving(true);
    try {
      const payload =
        game.scoringType === 'sets'
          ? { sets }
          : { matchScore: { scoreSideA: scoreA, scoreSideB: scoreB } };
      await padelService.saveScores(gameSlug, payload);
      setNotification({ isOpen: true, message: 'Skor berhasil disimpan', variant: 'success' });
      navigate(backUrl);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setNotification({
        isOpen: true,
        message: msg || 'Gagal menyimpan skor',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 py-6 max-w-3xl">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="mx-auto px-4 py-6 text-center">
        <p className="text-gray-600">Game tidak ditemukan</p>
        <ShadcnButton className="mt-4" onClick={() => navigate(backUrl)}>
          Kembali
        </ShadcnButton>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 max-w-3xl">
      <button
        type="button"
        onClick={() => navigate(backUrl)}
        className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4"
      >
        <Icon icon="mdi:arrow-left" className="w-4 h-4" />
        Kembali ke Statistik
      </button>

      <Card className="rounded-xl mb-6">
        <CardHeader className="pb-2">
          <p className="text-sm text-gray-500">{game.sessionTitle}</p>
          <h1 className="text-xl font-bold text-gray-900">Rekap Data</h1>
          <p className="text-sm text-gray-600">
            {playersLabel(game.sideAPlayers)} vs {playersLabel(game.sideBPlayers)}
          </p>
          <p className="text-sm font-medium text-[#FF6B00]">
            Status: {formatPadelGameStatus(game.status)}
          </p>
        </CardHeader>
        <CardContent>
          {game.scoringType === 'sets' ? (
            <PadelSetScorePanel
              game={game}
              sets={sets}
              canEdit={game.canEdit}
              onSetsChange={setSets}
            />
          ) : (
            <PadelMatchScorePanel
              game={game}
              scoreA={scoreA}
              scoreB={scoreB}
              canEdit={game.canEdit}
              onChange={(a, b) => {
                setScoreA(a);
                setScoreB(b);
              }}
            />
          )}
          {game.canEdit && (
            <div className="mt-6">
              <ShadcnButton
                className="bg-primary-500 w-full sm:w-auto"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </ShadcnButton>
            </div>
          )}
          {!game.canEdit && (
            <p className="text-sm text-gray-500 mt-4 text-center">Mode baca saja</p>
          )}
        </CardContent>
      </Card>

      {notification.isOpen && (
        <Notification
          isOpen={notification.isOpen}
          onClose={() => setNotification({ isOpen: false, message: '', variant: 'success' })}
          message={notification.message}
          variant={notification.variant}
        />
      )}
    </div>
  );
}
